import cx from 'classnames'
import React, { useEffect, useRef, useState } from 'react'

import { get } from '@js/common/api'
import { FetchStatus } from '@js/common/hooks/useStatus'

import { errorLogger } from '@f-fertighaus/utils/errorLogger'

interface LinkPreviewProps {
  message: string
}

interface PreviewData {
  title?: string | null
  description?: string | null
  image?: string | null
  url?: string
  status: FetchStatus
}

const LINK_PREVIEW_ENDPOINT = 'link-preview'
const URL_REGEX = /https?:\/\/[^\s"'<>]+(?:\/[^\s"'<>]+)+\/(?=[\s"'<>]|$)/gi

export const MessageWithLinkPreview = ({ message }: LinkPreviewProps) => {
  const [previewData, setPreviewData] = useState<PreviewData[]>([])
  const [displayPreview, setDisplayPreview] = useState(false)
  const seen = useRef<Set<string>>(new Set())

  useEffect(() => {
    const matches = message.match(URL_REGEX) || []
    const urls = Array.from(new Set(matches))
    urls.forEach(url => {
      if (seen.current.has(url)) {
        return
      }
      seen.current.add(url)

      setPreviewData(prev => [...prev, { url: url.trim(), status: 'fetching' }])

      get(`${LINK_PREVIEW_ENDPOINT}?url=${url}`)
        .then(resp => {
          const data = resp.data
          setPreviewData(prev =>
            prev.map(entry =>
              entry.url === url.trim()
                ? { ...entry, ...data, status: 'resolved' }
                : entry
            )
          )
        })
        .catch(err => {
          errorLogger(err)
          setPreviewData(prev =>
            prev.map(entry =>
              entry.url === url.trim() ? { ...entry, status: 'error' } : entry
            )
          )
        })
        .finally(() => {
          setDisplayPreview(true)
        })
    })
  }, [message])

  return (
    <>
      <DangerouslySetInnerHTML message={message} />
      <div
        className={cx(
          'f-d-flc f-fl-gap-010 f-m-t-020 f-fl-gap-005',
          displayPreview ? 'f-d-flr' : 'f-hidden'
        )}>
        {previewData.map((data, index) => {
          return {
            resolved: <LinkPreview previewData={data} key={index} />,
            error: null,
            fetching: <LinkPreviewSkeleton key={index} />,
            initial: null
          }[data.status]
        })}
      </div>
    </>
  )
}

const LinkPreviewContainer = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div
      className={cx('f-d-flr f-c-ba-bac010 f-fl-gap-010', className)}
      style={{ borderRadius: '4px', padding: '8px' }}>
      {children}
    </div>
  )
}

const LinkPreview = ({ previewData }: { previewData: PreviewData }) => {
  return (
    <a href={previewData.url} className='Chatbot-LinkPreview-link_container'>
      <LinkPreviewContainer>
        <img
          src={previewData.image}
          width={150}
          height={100}
          rel='preload'
          style={{ borderRadius: '4px', width: '40%' }}
          alt='preview'
        />
        <div
          style={{
            gap: '8px',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 3
          }}>
          <div className='f-t-micro f-t-bold' style={{ lineHeight: 1.33 }}>
            {previewData.title}
          </div>
          <div className='f-t-micro Chatbot-LinkPreview-description'>
            {previewData.description}
          </div>
        </div>
      </LinkPreviewContainer>
    </a>
  )
}

const LinkPreviewSkeleton = () => {
  return (
    <LinkPreviewContainer className='Chatbot-LinkPreview-skeleton'>
      <div
        className='f-c-ba-neu100'
        style={{
          width: '40%',
          height: '100px',
          borderRadius: '4px'
        }}></div>
      <div
        style={{
          gap: '24px',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 3
        }}>
        <div
          className='f-c-ba-neu100'
          style={{
            height: '20px',
            borderRadius: '4px'
          }}></div>
        <div
          className='f-c-ba-neu100'
          style={{
            height: '15px',
            borderRadius: '4px'
          }}></div>
      </div>
    </LinkPreviewContainer>
  )
}

const DangerouslySetInnerHTML = (props: { message: string }) => {
  return (
    <span
      dangerouslySetInnerHTML={{ __html: props.message }}
      data-isloading={props.message.length === 0}
    />
  )
}
